import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BarChartIcon from '@mui/icons-material/BarChart';
import BookIcon from '@mui/icons-material/Book';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChatIcon from '@mui/icons-material/Chat';
import EditIcon from '@mui/icons-material/Edit';
import HomeIcon from '@mui/icons-material/Home';
import MedicationIcon from '@mui/icons-material/Medication';
import MicIcon from '@mui/icons-material/Mic';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PeopleIcon from '@mui/icons-material/People';
import PhoneIcon from '@mui/icons-material/Phone';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  CardMedia,
  Chip,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import { styled } from '@mui/system';
import CAN from '../../../images/can.svg';
import AppleHealth from '../../../images/appleHealth.svg';
import { useQuery } from 'react-query';
import {
  getAllergiesApi,
  getDiagnosisApi,
  getDoctorApi,
  getRecipientMedications,
  getUserDevices,
  getUserInsuranceApi,
} from '../../../utils/api';
import { isEmpty } from 'lodash';

const StyledCardMedia = styled(CardMedia)({
  width: '60px',
  height: '60px',
  borderRadius: '16px',
  overflow: 'hidden',
  border: '1px solid rgba(229, 229, 234, 1)',
  objectFit: 'contain',
  backgroundColor: '#fff',
});

const General = ({ user, recipient }) => {
  const [doctors, setDoctors] = React.useState({});
  const [insurance, setInsurance] = React.useState({});
  const [device, setDevice] = React.useState({});

  const { data: diagnosis, isLoading: diagnosisLoader } = useQuery(
    ['getDiagnosisApi', recipient],
    () => getDiagnosisApi({ userId: user?._id, recipientId: recipient?._id }),
    {
      retry: false,
      cacheTime: 0,
    },
  );

  const { data: allergy, isLoading: allergyLoader } = useQuery(
    ['getAllergiesApi', recipient],
    () => getAllergiesApi({ userId: user?._id, recipientId: recipient?._id }),
    {
      retry: false,
      cacheTime: 0,
    },
  );

  const { isLoading: doctorLoader } = useQuery(
    ['getDoctorApi', recipient],
    () => getDoctorApi({ userId: user?._id, recipientId: recipient?._id }),
    {
      onSuccess: ({ data }) => {
        if (!isEmpty(data)) {
          setDoctors(data[0]);
        }
      },
      retry: false,
      cacheTime: 0,
    },
  );

  const { isLoading: insuranceLoader } = useQuery(
    ['getUserInsuranceApi', recipient],
    () => getUserInsuranceApi({ userId: user?._id, recipientId: recipient?._id }),
    {
      onSuccess: ({ data }) => {
        if (!isEmpty(data)) {
          setInsurance(data[0]);
        }
      },
      retry: false,
      cacheTime: 0,
    },
  );

  const { isLoading: deviceLoader } = useQuery(
    ['getUserDevices', recipient],
    () => getUserDevices({ userId: user?._id || user?.id, selectedRecipient: recipient?._id }),
    {
      onSuccess: ({ data }) => {
        if (!isEmpty(data)) {
          setDevice(data[0]);
        }
      },
      retry: false,
      cacheTime: 0,
    },
  );

  const { data: medications, isLoading: medicationLoader } = useQuery(
    ['getRecipientMedications', recipient],
    () => getRecipientMedications({ userId: user?._id, recipientId: recipient?._id }),
    {
      retry: false,
      cacheTime: 0,
    },
  );

  return (
    <Grid container direction={'row'} sx={{ mb: 5 }}>
      <Grid item xs={12} md={6} lg={4} sm={12}>
        <Paper sx={{ p: 2, borderRadius: '20px', boxShadow: 'none', mr: 1 }}>
          {!insuranceLoader && isEmpty(insurance) && <Typography variant="h6">No insurance found!</Typography>}
          {!insuranceLoader && !isEmpty(insurance) && (
            <>
              <img src={insurance?.imageUrl} alt={insurance?.title} style={{ width: '120px', height: 'auto' }} />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  mt: 2,
                }}
              >
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box
                    sx={{
                      flex: 1,
                      p: 2,
                      bgcolor: '#f5f5f7',
                      borderRadius: '10px',
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Member ID
                    </Typography>
                    <Typography variant="body1">{insurance?.member}</Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      p: 2,
                      bgcolor: '#f5f5f7',
                      borderRadius: '10px',
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Group #
                    </Typography>
                    <Typography variant="body1">{insurance?.group}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box
                    sx={{
                      flex: 1,
                      p: 2,
                      bgcolor: '#f5f5f7',
                      borderRadius: '10px',
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Plan
                    </Typography>
                    <Typography variant="body1">{insurance?.plan}</Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      p: 2,
                      bgcolor: '#f5f5f7',
                      borderRadius: '10px',
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Effective Date
                    </Typography>
                    <Typography variant="body1">{insurance?.date}</Typography>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Paper>
        {!isEmpty(doctors) && (
          <Paper sx={{ p: 2, borderRadius: '20px', boxShadow: 'none', mt: 3, mr: 1 }}>
            <Typography variant="h6">{doctors?.name}</Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                mt: 2,
              }}
            >
              <Typography variant="body2" color="textSecondary">
                Phone number
              </Typography>
              <Typography variant="body1">{doctors?.phoneNo}</Typography>
              <Typography variant="body2" color="textSecondary">
                Address
              </Typography>
              <Typography variant="body1">{doctors?.address}</Typography>
              <Typography variant="body2" color="textSecondary">
                Available
              </Typography>
              <Typography variant="body1">{doctors?.availability}</Typography>
            </Box>
          </Paper>
        )}
        {isEmpty(doctors) && (
          <Paper sx={{ p: 2, borderRadius: '20px', boxShadow: 'none', mt: 3, mr: 1 }}>
            <Typography variant="h6">No Doctor Found!</Typography>
          </Paper>
        )}
      </Grid>
      <Grid item xs={12} md={6} lg={4} sm={12}>
        <Paper sx={{ p: 4, borderRadius: '20px', boxShadow: 'none', mr: 1 }}>
          <Typography variant="h6">Allergies</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            {!allergyLoader && isEmpty(allergy?.data) && <Typography variant="body1">No allergy found!</Typography>}
            {!allergyLoader &&
              !isEmpty(allergy?.data) &&
              allergy?.data?.slice(0, 2)?.map((item, idx) => <Chip label={item?.title} key={idx} />)}
            {allergy?.data?.length > 2 && <Chip label={`+${allergy?.data?.length - 2}`} />}
          </Box>
        </Paper>
        <Paper sx={{ p: 4, borderRadius: '20px', mt: 1, boxShadow: 'none', mr: 1 }}>
          <Typography variant="h6">Medication</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            {!medicationLoader && isEmpty(medications?.data) && (
              <Typography variant="body1">No allergy found!</Typography>
            )}
            {!medicationLoader &&
              !isEmpty(medications?.data) &&
              medications?.data
                ?.slice(0, 2)
                ?.map((item, idx) => <Chip label={item?.name} icon={<MedicationIcon key={idx} />} />)}
            {medications?.data?.length > 2 && <Chip label={`+${medications?.data?.length - 2}`} />}
          </Box>
        </Paper>
        <Paper sx={{ p: 2, borderRadius: '20px', boxShadow: 'none', mt: 3, mr: 1 }}>
          <Typography variant="h6">{recipient?.fullName}’s tools</Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mt: 2,
            }}
          >
            {!isEmpty(device?._id) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <StyledCardMedia component="img" image={CAN} alt="CAN" />
                <Typography variant="body1">CAN Go</Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6} lg={4} sm={12}>
        <Paper sx={{ p: 2, borderRadius: '20px', boxShadow: 'none' }}>
          <Typography variant="h6">Diagnoses</Typography>
          {!diagnosisLoader && isEmpty(diagnosis?.data) && (
            <Box sx={{ mt: 2, bgcolor: '#f5f5f7', p: 2 }}>
              <Typography variant="body1">No Diagnosis found!</Typography>
            </Box>
          )}
          {!diagnosisLoader &&
            !isEmpty(diagnosis?.data) &&
            diagnosis?.data?.slice(0, 2)?.map((item, idx) => (
              <div key={idx}>
                <Box sx={{ mt: 2, bgcolor: '#f5f5f7', p: 2 }}>
                  <Typography variant="body1">{item?.title}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {item?.description}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Date of Diagnosis:
                    <Typography variant="body1" component="span">
                      {item?.date}
                    </Typography>
                  </Typography>
                </Box>
                {diagnosis?.data?.length !== 1 && <Divider sx={{ my: 2 }} />}
              </div>
            ))}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default General;
